import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Heart, Building2, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { stats } from '../mockData';
import { IVFSuccessEstimator } from '../components/IVFSuccessEstimator';

export const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const coreServices = [
    {
      icon: Heart,
      emoji: "🧬",
      title: "Reproductive & IVF Analytics",
      description: "Specialized statistical support for fertility clinics to improve clinical outcomes, reporting, and decision-making.",
      features: [
        "IVF outcome and cycle analysis",
        "KPI dashboards for clinic and lab performance",
        "Predictive modelling for treatment success",
        "Annual success rate reports (regulatory-ready)"
      ]
    },
    {
      icon: Building2,
      emoji: "🏥",
      title: "Clinical & Healthcare Analytics",
      description: "Advanced analytics to improve patient outcomes, operational efficiency, and data-driven healthcare management.",
      features: [
        "Clinical data audit and quality assessment",
        "Patient outcome analysis and benchmarking",
        "KPI dashboards for departments",
        "Survey and patient satisfaction analysis"
      ]
    },
    {
      icon: GraduationCap,
      emoji: "📊",
      title: "Research & Academic Support",
      description: "End-to-end statistical support for research, thesis, and publication success.",
      features: [
        "Study design and sample size calculation",
        "Data analysis using R, SPSS, STATA, Python",
        "Manuscript statistical review (journal-ready)",
        "Systematic review and meta-analysis (PRISMA)"
      ]
    }
  ];

  const whyChoosePoints = [
    '3+ years working inside UAE fertility hospitals. We understand IVF data because we\'ve lived in it, not just analysed it from the outside',
    'Every engagement is anchored to a measurable outcome. Not a report, a recommendation, or a methodology document',
    'We work across the full analytical stack: R, Python, SPSS, STATA, dashboard development, predictive modelling. You get the right tool for your specific question',
    'Aligned with DHA, DOH, MOH, and JCI frameworks. Your outputs are regulatory-ready from day one',
    'Plain language reporting for clinical and management audiences. The findings land with the people who need to act on them, not just the people who commissioned the analysis'
  ];

  const whoWeWorkWith = [
    'Fertility clinics and IVF centers',
    'Hospitals and healthcare providers',
    'Healthcare startups',
    'Clinical research teams',
    'Academic researchers and students'
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>StatZen Analytics - Transforming Data into Clinical Insight</title>
        <meta name="description" content="Statzen Analytics, Healthcare data consultancy in Abu Dhabi, UAE. Specialized in IVF clinic analytics, biostatistics, and clinical research data solutions." />
        <link rel="canonical" href="https://statzenanalytics.com/" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "ProfessionalService",
              "name": "Statzen Analytics",
              "url": "https://statzenanalytics.com",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Abu Dhabi",
                "addressCountry": "AE"
              },
              "description": "Healthcare data and biostatistics consultancy specializing in IVF clinic analytics in the UAE."
            }
          `}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div
            className={`text-center transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Your Clinical Data
              <span className="block mt-2">Is Telling You Something.</span>
            </h1>
            <p className="text-2xl md:text-3xl font-semibold mb-6 max-w-3xl mx-auto">
              <span className="text-blue-600">Are You Listening to It?</span>
            </p>
            <p className="text-lg text-gray-600 mb-4 max-w-3xl mx-auto">
              Most healthcare clinics and fertility centers collect more data than they know what to do with. We turn that data into decisions, identifying where revenue is leaking, which patients are at risk, and what your numbers are actually telling you.
            </p>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
              StatZen Analytics specialises in healthcare biostatistics and clinical analytics for IVF clinics, hospitals, polyclinics, and research teams across the UAE.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/case-studies">
                <Button
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white transition-colors duration-300"
                >
                  See What We've Built
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50 transition-colors duration-300"
                >
                  Book a Free Consultation
                </Button>
              </Link>
            </div>
          </div>

          {/* Highlights Section */}
          <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`text-center bg-white p-6 rounded-lg shadow-sm border border-gray-200 transition-all duration-700 delay-${index * 100} ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
              >
                <div className="text-3xl md:text-4xl font-bold text-blue-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Short Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            We Find What Your Data Is Costing You
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Every clinic has performance gaps. Most of them are invisible, not because the data doesn't exist, but because nobody has structured it into a question worth answering.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            A 7% cycle cancellation rate sounds like a clinical statistic. It's also approximately 50 cycles per year that didn't reach retrieval. A 57% first-visit non-return rate sounds like a patient behaviour problem. It's actually a scheduling problem, and the data shows exactly where.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            We work with fertility clinics, hospitals, specialty clinics, polyclinics, and research teams across the UAE to find those gaps and quantify what closing them is worth.
          </p>
        </div>
      </section>

      {/* Core Services Overview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Each engagement starts with your data and ends with a decision. Here's what that looks like across our three core service areas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {coreServices.map((service, index) => (
              <Card
                key={index}
                className="hover:shadow-xl transition-shadow duration-300 border-gray-200"
              >
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">{service.emoji}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600">
                        <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Link to="/services">
              <Button
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50 transition-colors duration-300"
              >
                View All Services
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Choose Us
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We combine statistical expertise with real-world clinical understanding to deliver 
                practical, reliable, and high-impact insights.
              </p>
              <ul className="space-y-4">
                {whyChoosePoints.map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80"
                alt="Data Analytics Dashboard"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* IVF Success Estimator */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <IVFSuccessEstimator />
        </div>
      </section>

      {/* Who We Work With */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Who We Work With
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Every segment has different data, different regulatory requirements, and different questions worth answering. We work across all of them.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {whoWeWorkWith.map((client, index) => (
              <div
                key={index}
                className="flex items-center space-x-3 bg-blue-50 p-6 rounded-lg border border-blue-100 hover:border-blue-300 hover:shadow-md transition-all duration-300"
              >
                <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0" />
                <span className="text-lg font-medium text-gray-900">{client}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to See What Your Data Is Actually Telling You?
          </h2>
          <p className="text-lg text-blue-100 mb-8">
            Most clinics have more useful data than they realise — and more performance gaps than their current reporting shows. A free 30-minute consultation is where we start: your data, your questions, your numbers.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-white text-blue-600 hover:bg-gray-100 transition-colors duration-300 flex items-center justify-center mx-auto space-x-2"
            >
              <span>Book a Free Consultation</span>
              <span className="text-xl">👉</span>
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
